import style from "@/styles/pages/DownPayment/DownPayment.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import CurrencyInput from "react-currency-input-field";
import { setApplicationProgress, setDownPayment } from "@/slices/applicationSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import { selectPropertyInfo } from "@/slices/propertyInfoSlice";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";

export default function DownPayment() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [downPaymentAmount, setDownPaymentAmount] = useState("");

    const propertyInfo = useSelector(selectPropertyInfo).propertyInfo;
    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    const methods = useForm({
        defaultValues: {
            downPayment: ""
        },
    });

    useEffect(() => {
        document.title = "Enter Down Payment";
        methods.setValue("downPayment", application ? application.downPayment == 0 ? "" : application.downPayment.toString() : "");
        setDownPaymentAmount(application.downPayment == 0 ? "" : application.downPayment.toString());
        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

        client.apiApplicationsFetch().then((result) => {
            result.application = application;
            result.propertyInfo = propertyInfo;
            client.apiApplicationsSave(new ApplicationDto({
              applicationData: JSON.stringify(result)
            })).catch((error) => {
              console.log("Error saving application info");
            });
          }).catch((error) => {
            console.log("Error fetching application info", error);
          });
    }, []);

    const onError = (errors: object) => {
        setClickInsightFormErrors(errors);
    }

    const onSubmit = (data: any) => {
        if (+downPaymentAmount > propertyInfo.purchasePrice){
            methods.setError("downPayment", { type: "custom", message: "Your down payment cannot exceed the purchase price of the property." });
            return;
        }

        dispatch(setDownPayment(+downPaymentAmount));
        dispatch(setApplicationProgress(4));
        setClickInsightApplicationProgress('Your new mortgage - How much have you saved for your down payment?');
        navigate("/single/mortgage-term");
    };

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <div className="max-md:ml-2">
                            <h1 aria-label="Address Header">How much have you saved for your down payment?</h1>
                            <p className="sub-heading">Make sure your down payment amount meets the minimum requirement for your purchase price.</p>
                        </div>
                        <div className={"flex flex-row mt-5"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className="flex md:flex-row flex-col w-max">
                                        <div className="mt-5">
                                            <label htmlFor="downPayment">Down Payment Amount</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.downPayment?.message
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[195px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <CurrencyInput
                                                    id="downPayment"
                                                    placeholder="$"
                                                    prefix={"$"}
                                                    decimalsLimit={2}
                                                    decimalScale={2}
                                                    allowNegativeValue={false}
                                                    maxLength={10}
                                                    value={downPaymentAmount}
                                                    className={clsx(
                                                        methods.formState?.errors?.downPayment?.message ? "error" : "",
                                                        "mt-2 w:[220px] md:w-[222px]"
                                                    )}
                                                    {...methods.register("downPayment", {
                                                        required: "Enter an amount of the down payment.",
                                                    })}
                                                    onValueChange={(value, name, values) => {
                                                        value != undefined ? setDownPaymentAmount(value) : setDownPaymentAmount("");
                                                        methods.clearErrors();
                                                    }}
                                                />
                                            </div>
                                            {methods.formState?.errors?.downPayment?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState?.errors?.downPayment?.message.toString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <p className="sub-heading mt-5" style={{ fontSize: 14 }}>
                                        <a className="brand-blue-text" style={{textDecorationLine: "underline"}} href="https://www.meridiancu.ca/good-sense/posts/everything-you-need-to-know-about-down-payments-when-purchasing-a-home" target="_blank" >Learn more</a> about down payment requirements.
                                    </p>

                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}